import { getAccessServiceDescription } from 'app/domain/AccessServiceTypes';
import { Accessibility, AssociatedContentItemFragment } from 'app/graphql/__generated__/types/contento';
import Link from 'next/link';
import React, { useState } from 'react';
import { fontWeightBold } from '../../../../ui-components/design-tokens';
import PlayBadge from '../../../../ui-components/PlayBadge/PlayBadge';
import { SvgIcons } from '../../../../ui-components/SvgIcon/SvgIcon';
import { SIZE } from '../../../../ui-components/typography/PlayText/PlayText';
import { VisuallyHiddenStyle } from '../../../../ui-components/utils.styles';
import { contentoImageUrl } from '../../../../utils/svtimage';
import useTrackImpression from '../../../../utils/useTrackImpression';
import OpenModal from '../../../VideoModal/OpenModal';
import {
    AccessibilitySvgIcon,
    AccessibilityVariants,
    AssociatedMoreInfoLink,
    AssociatedProgressBar,
    AssoiciatedContentImage,
    Content,
    ContentDescription,
    ContentHeader,
    ContentLink,
    ContentSubheader,
    ExpandButton,
    ImageContainer,
    ImageLink,
    Information,
    MobileDescription,
    OutsideSwedenContainer,
    OutsideSwedenText,
    StyledPlayIcon,
    UpcomingOverlay,
    UpcomingOverlayText,
} from './AssociatedContent.styles';
import { upcomingDateFormatted } from './util';

type AssociatedContentItemProps = {
    content: AssociatedContentItemFragment;
    type: string;
    selectedVideoId: string;
    progress: number;
    trackClick: (e: React.MouseEvent<HTMLAnchorElement>, leadsToPlayable: boolean) => void;
    trackImpression: any;
    index: any;
};

const AssociatedContentItem = ({
    content,
    type,
    selectedVideoId,
    progress,
    trackClick,
    trackImpression,
    index,
}: AssociatedContentItemProps) => {
    const [trackingRef] = useTrackImpression(trackImpression, index + 1, content.item.svtId);
    const imageSrc = contentoImageUrl(content.images.wide, 'large', 600);
    const isUpcoming = type === 'upcoming';
    const [expanded, setExpanded] = useState(false);

    const accessibilities = content.item.accessibilities.filter((accessibility) =>
        [Accessibility.AudioDescribed, Accessibility.SignInterpreted].includes(accessibility)
    );

    const progressPercentage = progress ? progress[content.item.svtId] : null;
    const accessibleLabel = `Spela ${content.heading}`;

    const getIcon = (accessibility: Accessibility) => {
        if (accessibility === Accessibility.SignInterpreted) {
            return SvgIcons.SIGN_LANGUAGE;
        }

        if (accessibility === Accessibility.AudioDescribed) {
            return SvgIcons.SIGHT;
        }

        return null;
    };

    return (
        <Content
            selected={content.item.videoSvtId === selectedVideoId}
            className={`lp_pos${index + 1}`}
            ref={trackingRef}
        >
            <ImageContainer>
                {isUpcoming ? (
                    <ImageLink
                        as="div"
                        isUpcoming
                        aria-label=""
                        isOnlyAvailableInSweden={content.item.restrictions.onlyAvailableInSweden}
                    >
                        <AssoiciatedContentImage
                            src={imageSrc}
                            alt=""
                            blurred={content.item.restrictions.onlyAvailableInSweden}
                            opacity={0.2}
                        >
                            {content.item.restrictions.onlyAvailableInSweden && (
                                <OutsideSwedenContainer>
                                    <OutsideSwedenText weight={fontWeightBold}>
                                        Kan bara ses i Sverige
                                    </OutsideSwedenText>
                                </OutsideSwedenContainer>
                            )}
                        </AssoiciatedContentImage>
                        {'validFromFormatted' in content.item && (
                            <UpcomingOverlay
                                onlyAvailableInSweden={content.item.restrictions.onlyAvailableInSweden}
                                aria-hidden
                            >
                                <UpcomingOverlayText bold>
                                    {upcomingDateFormatted(content.item.validFromFormatted)[0]}
                                </UpcomingOverlayText>
                                <UpcomingOverlayText>
                                    {upcomingDateFormatted(content.item.validFromFormatted)[1]}
                                </UpcomingOverlayText>
                            </UpcomingOverlay>
                        )}
                    </ImageLink>
                ) : (
                    <OpenModal videoId={content.item.videoSvtId} videoUrl={content.item.urls.svtplay}>
                        {(onClick) => (
                            <ImageLink
                                tabIndex={-1}
                                isUpcoming={false}
                                aria-label={accessibleLabel}
                                onClick={(e) => {
                                    onClick();
                                    trackClick(e, true);
                                }}
                                data-rt="associated-image-link"
                                isOnlyAvailableInSweden={content.item.restrictions.onlyAvailableInSweden}
                            >
                                <StyledPlayIcon icon={SvgIcons.PLAY} width={50} />
                                <AssoiciatedContentImage
                                    src={imageSrc}
                                    alt=""
                                    blurred={content.item.restrictions.onlyAvailableInSweden}
                                >
                                    {content.badge && (
                                        <PlayBadge
                                            text={content?.badge?.text}
                                            theme={content?.badge?.type}
                                            accessibilityText={content?.badge?.altText}
                                            overlay
                                        />
                                    )}
                                    {content.item.restrictions.onlyAvailableInSweden && (
                                        <OutsideSwedenContainer>
                                            <OutsideSwedenText weight={fontWeightBold}>
                                                Kan bara ses i Sverige
                                            </OutsideSwedenText>
                                        </OutsideSwedenContainer>
                                    )}
                                </AssoiciatedContentImage>
                                {progressPercentage ? (
                                    <AssociatedProgressBar progress={progressPercentage} selected />
                                ) : null}
                            </ImageLink>
                        )}
                    </OpenModal>
                )}
            </ImageContainer>
            <Information isUpcoming={isUpcoming}>
                <ContentHeader as="h3">
                    {isUpcoming ? (
                        <>
                            <span aria-hidden="true">{content.heading}</span>
                            {'nameRaw' in content.item && 'positionInSeason' in content.item && (
                                <VisuallyHiddenStyle as="span">
                                    {content.item.nameRaw
                                        ? `${content.item.positionInSeason}. ${content.item.nameRaw}`
                                        : content.heading}
                                </VisuallyHiddenStyle>
                            )}
                        </>
                    ) : (
                        <Link
                            href={{
                                pathname: content.item.urls.svtplay,
                                query: {
                                    info: 'visa',
                                },
                            }}
                            passHref
                        >
                            <ContentLink
                                aria-label={accessibleLabel}
                                onClick={(e) => trackClick(e, false)}
                                data-rt="associated-header-link"
                            >
                                {content.heading}
                            </ContentLink>
                        </Link>
                    )}
                </ContentHeader>
                <ContentSubheader size={SIZE.SMALL}>
                    {'validFromFormatted' in content.item && (
                        <VisuallyHiddenStyle as="span">
                            {`${isUpcoming ? 'Publiceras' : 'Publicerades'}: ${
                                content.item.validFromFormatted
                            }`}
                        </VisuallyHiddenStyle>
                    )}
                    <span aria-hidden>{content.subHeading}</span>
                    {accessibilities.length > 0 && (
                        <AccessibilityVariants>
                            <VisuallyHiddenStyle as="span">
                                {`Finns som ${accessibilities
                                    .map((accessibility) => getAccessServiceDescription(accessibility))
                                    .join(' och ')}`}
                            </VisuallyHiddenStyle>
                            {accessibilities.map((accessibility) => (
                                <AccessibilitySvgIcon
                                    key={`accessibility-${accessibility}`}
                                    icon={getIcon(accessibility)}
                                    width={18}
                                />
                            ))}
                        </AccessibilityVariants>
                    )}
                </ContentSubheader>
                <ContentDescription size={SIZE.LARGE} expanded={expanded} isUpcoming={isUpcoming}>
                    {content.description}
                    {!isUpcoming && (
                        <Link
                            href={{
                                pathname: content.item.urls.svtplay,
                                query: {
                                    info: 'visa',
                                },
                            }}
                            passHref
                        >
                            <AssociatedMoreInfoLink
                                data-rt="associated-more-info-link"
                                onClick={(e) => trackClick(e, false)}
                            >
                                Mer om programmet
                            </AssociatedMoreInfoLink>
                        </Link>
                    )}
                </ContentDescription>
            </Information>
            <ExpandButton
                expanded={expanded}
                isUpcoming={isUpcoming}
                onClick={() => setExpanded(!expanded)}
                aria-label={`Visa beskrivning för ${content.heading}`}
            />

            <MobileDescription expanded={expanded} size={SIZE.LARGE}>
                {content.description}
                {!isUpcoming && (
                    <Link
                        href={{
                            pathname: content.item.urls.svtplay,
                            query: {
                                info: 'visa',
                            },
                        }}
                        passHref
                    >
                        <AssociatedMoreInfoLink onClick={(e) => trackClick(e, false)}>
                            Mer om programmet
                        </AssociatedMoreInfoLink>
                    </Link>
                )}
            </MobileDescription>
        </Content>
    );
};

export default AssociatedContentItem;
